import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export const DATA_TABLE_HEADER_CLASS = "heading-6 px-4 text-zinc-900";

declare module "@tanstack/react-table" {
    // biome-ignore lint/correctness/noUnusedVariables: required by TanStack's meta interface
    interface ColumnMeta<TData, TValue> {
        headerClassName?: string;
        cellClassName?: string;
    }
}

type Props<TData> = {
    data: TData[];
    columns: ColumnDef<TData, unknown>[];
    getRowId: (row: TData) => string;
    emptyMessage?: string;
    initialSorting?: SortingState;
    className?: string;
};

/**
 * Sortable data table shell around TanStack Table + the shared Table primitives.
 * Column cells and domain logic stay in the host view.
 */
export function DataTable<TData>({
    data,
    columns,
    getRowId,
    emptyMessage = "No results.",
    initialSorting = [],
    className,
}: Props<TData>) {
    const [sorting, setSorting] = useState<SortingState>(initialSorting);

    const table = useReactTable({
        data,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId,
        enableSortingRemoval: false,
    });

    const rows = table.getRowModel().rows;
    const columnCount = table.getAllColumns().length;

    return (
        <div
            className={cn(
                "min-h-0 flex-1 overflow-auto [&_[data-slot=table-container]]:overflow-visible",
                className,
            )}
        >
            <Table className="table-fixed">
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow
                            key={headerGroup.id}
                            className="[&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white"
                        >
                            {headerGroup.headers.map((header) => (
                                <TableHead
                                    key={header.id}
                                    className={cn(
                                        DATA_TABLE_HEADER_CLASS,
                                        "overflow-hidden",
                                        header.column.columnDef.meta?.headerClassName,
                                    )}
                                    aria-sort={
                                        header.column.getIsSorted() === "asc"
                                            ? "ascending"
                                            : header.column.getIsSorted() === "desc"
                                              ? "descending"
                                              : "none"
                                    }
                                    style={{ width: header.getSize() }}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext(),
                                          )}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columnCount}
                                className="px-4 py-10 text-center text-sm text-zinc-400 leading-body-sm"
                            >
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row) => (
                            <TableRow key={row.id} className="even:bg-zinc-50">
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        className={cn(
                                            cell.column.columnDef.meta?.cellClassName,
                                        )}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

/** Sortable column header button for use inside column `header` renderers. */
export function SortableHeader({
    label,
    sorted,
    column,
}: {
    label: string;
    sorted: false | "asc" | "desc";
    column: {
        toggleSorting: (desc?: boolean) => void;
    };
}) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="group h-auto max-w-full justify-start gap-1 rounded-sm px-0 py-0 text-left font-medium hover:bg-transparent hover:text-zinc-700"
            onClick={() => column.toggleSorting(sorted === "asc")}
        >
            <span className="truncate">{label}</span>
            {sorted === "asc" ? (
                <ArrowDown className="size-3.5 shrink-0 text-zinc-500" />
            ) : sorted === "desc" ? (
                <ArrowUp className="size-3.5 shrink-0 text-zinc-500" />
            ) : (
                <ArrowUpDown className="size-3.5 shrink-0 text-zinc-300 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />
            )}
        </Button>
    );
}
